import { BrainSearchInput_ContentUsageFragment$key } from "@/brain-search/internal/__generated__/BrainSearchInput_ContentUsageFragment.graphql"
import SendIcon from "@/core/ui/iconsax/linear/arrow-right.svg"
import StopIcon from "@/core/ui/iconsax/linear/pause.svg"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import styleIf from "@assets/style/util/styleIf"
import {
  DiscoIcon,
  DiscoIconButton,
  DiscoInput,
  DiscoInputProps,
  DiscoText,
} from "@disco-ui"
import { lighten, useTheme } from "@material-ui/core"
import { useIsMobile } from "@utils/hook/screenSizeHooks"
import { TestIDProps } from "@utils/typeUtils"
import classNames from "classnames"
import { useState } from "react"
import { graphql, useFragment } from "react-relay"

interface BrainSearchInputProps
  extends TestIDProps,
    Pick<DiscoInputProps, "disabled" | "multiline" | "placeholder"> {
  onSubmit: (input: string) => void
  onChange?: (input: string) => void
  onAbort?: () => void
  isGenerating?: boolean
  clearOnSubmit?: boolean
  classes?: {
    root?: string
  }
  variant?: "raised" | "flat"
  contentUsageKey?: BrainSearchInput_ContentUsageFragment$key | null
}

function BrainSearchInput({
  placeholder = "Ask a question...",
  onSubmit,
  onChange,
  onAbort,
  isGenerating = false,
  classes: customClasses,
  multiline = true,
  clearOnSubmit = true,
  variant = "raised",
  contentUsageKey,
}: BrainSearchInputProps) {
  const [input, setInput] = useState("")
  const isMobile = useIsMobile()
  const theme = useTheme()

  const contentUsage = useFragment<BrainSearchInput_ContentUsageFragment$key>(
    graphql`
      fragment BrainSearchInput_ContentUsageFragment on ContentUsage {
        id
        content {
          name
        }
      }
    `,
    contentUsageKey || null
  )
  const hasSources = !!contentUsage
  const classes = useStyles({ variant, hasSources })

  const noPrompt = input.trim().length === 0

  return (
    <DiscoInput
      placeholder={placeholder}
      classes={{
        root: classNames(classes.inputRoot, customClasses?.root),
        input: classes.input,
      }}
      value={input}
      onChange={handleChange}
      multiline={multiline}
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus={!isMobile}
      endAdornment={
        hasSources ? (
          <div className={classes.endAdornment}>
            <div className={classes.sources}>
              <div className={classes.sourceTag}>
                <DiscoIcon icon={"file"} color={theme.palette.groovy.neutral[500]} />
                <DiscoText
                  variant={"body-xs-500"}
                  color={"groovy.neutral.500"}
                  truncateText={1}
                >
                  {contentUsage.content.name}
                </DiscoText>
              </div>
            </div>
            {renderButton()}
          </div>
        ) : (
          renderButton()
        )
      }
      onKeyPress={(e) => {
        if (noPrompt) return

        if (e.key === "Enter" && !e.shiftKey) {
          handleSubmit(e)
        }
      }}
    />
  )

  function renderButton() {
    return isGenerating ? (
      <DiscoIconButton
        className={classes.sendButton}
        tooltip={"Stop Generating"}
        onClick={onAbort}
      >
        <StopIcon className={classes.stopIcon} />
      </DiscoIconButton>
    ) : (
      <DiscoIconButton
        className={classes.sendButton}
        disabled={noPrompt}
        haveDisabledStyles
        variant={"primary"}
        onClick={(e) => handleSubmit(e)}
      >
        <SendIcon className={classes.sendIcon} color={theme.palette.common.white} />
      </DiscoIconButton>
    )
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setInput(e.target.value)
    onChange?.(e.target.value)
  }

  function handleSubmit(
    e: React.KeyboardEvent<HTMLDivElement> | React.MouseEvent<HTMLButtonElement>
  ) {
    onSubmit(input)
    e.preventDefault()
    if (clearOnSubmit) setInput("")
  }
}

type StyleProps = {
  variant: "raised" | "flat"
  hasSources: boolean
}

const useStyles = makeUseStyles((theme) => ({
  inputRoot: ({ variant, hasSources }: StyleProps) => ({
    borderRadius: theme.measure.borderRadius.large,
    boxShadow: variant === "raised" ? theme.palette.groovyDepths.raisedBoxShadow : "none",
    width: "100%",
    padding: theme.spacing(1),
    minHeight: "48px",
    maxWidth: "720px",
    position: "relative",
    left: `-8px`,
    ...styleIf(variant === "flat", {
      border: theme.palette.constants.borderSmall,
      backgroundColor: theme.palette.background.paper,
      "&:hover:not(.Mui-focused)": {
        backgroundColor: theme.palette.background.paper,
      },
    }),
    ...styleIf(hasSources, {
      flexDirection: "column",
      gap: theme.spacing(0.5),
    }),
  }),
  input: {
    padding: theme.spacing(0.75, 1),
    maxHeight: "104px",
    overflowY: "auto !important",
    flex: "1 1 auto",
    width: `calc(100% - ${theme.spacing(2)}px)`,
  },
  sendButton: {
    borderRadius: theme.measure.borderRadius.medium,
    padding: theme.spacing(0.5),
    marginLeft: theme.spacing(1),
    alignSelf: "flex-end",
    height: "32px",
    width: "32px",
    backgroundColor: theme.palette.primary.main,

    "&:hover": {
      backgroundColor: lighten(theme.palette.primary.main, 0.2),
    },
  },
  sendIcon: {
    transform: "rotate(-90deg)",
  },
  stopIcon: {
    color: `${theme.palette.common.white} !important`,
  },
  endAdornment: {
    width: "100%",
    display: "flex",
  },
  sources: {
    flexGrow: 1,
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    minWidth: 0,
  },
  sourceTag: {
    display: "flex",
    gap: theme.spacing(0.5),
    alignItems: "center",
    borderRadius: theme.measure.borderRadius.medium,
    maxWidth: "100%",
    padding: theme.spacing(1),
    backgroundColor: theme.palette.groovy.neutral[100],
    color: theme.palette.groovy.neutral[500],
    border: `1px solid ${theme.palette.groovy.neutral[200]}`,
    "& svg": {
      width: 16,
      height: 16,
      flexShrink: 0,
    },
  },
}))

export default BrainSearchInput
